import React, { useMemo } from "react";
import submit from "submit-form";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import SvgIcon from "../../../../../parts/SvgIcon";
import { MealPlannerMealTypeHeader } from "./MealPlannerMealTypeHeader";
import { MealPlannerCalendar } from "./MealPlannerCalendar";

/**
 * MealPlannerSimplifiedCalendar
 *
 * Component for displaying meal type containers in simplified meal planner
 * Shows meal type headers for each meal type found in the options
 */
export const MealPlannerSimplifiedCalendar = ({
  mealPlan,
  tabState,
  viewMode = false,
  isFromNutritionPlan = false,
  query,
  basicMealsInformation,
}) => {
  const { t } = useTranslation("athlete-portal");
  const basicMealTypes = ["Breakfast", "Lunch", "Snack", "Dinner"];

  // Get the number of options based on tabState
  const numberOfOptions = useMemo(() => {
    const optionsArray =
      tabState === "training"
        ? mealPlan?.trainingDayOptions || []
        : mealPlan?.restDayOptions || [];
    return optionsArray.length;
  }, [mealPlan, tabState]);

  // Get options array based on tabState
  const optionsArray = useMemo(() => {
    return tabState === "training"
      ? mealPlan?.trainingDayOptions || []
      : mealPlan?.restDayOptions || [];
  }, [mealPlan, tabState]);

  // Get meal types from the options based on tabState
  const mealTypes = useMemo(() => {
    // Get the first available option from training or rest
    const firstOption =
      mealPlan?.trainingDayOptions?.[0] || mealPlan?.restDayOptions?.[0];

    // If there's an option with meals, get meal types from it
    if (firstOption?.meals && firstOption.meals.length > 0) {
      return firstOption.meals
        .sort((a, b) => a.order - b.order)
        .map((meal) => meal.type);
    }

    // If no options or no meals, return default meal types
    return basicMealTypes;
  }, [mealPlan]);

  // Create dummy options data for testing layout (if no real options exist)
  const displayOptions = useMemo(() => {
    if (optionsArray.length > 0) {
      return optionsArray;
    }
    // Create dummy options for layout testing
    return Array.from({ length: 3 }, (_, i) => ({
      _id: `dummy-option-${i + 1}`,
      title: t("mealPlannerSimplifiedCalendar.optionLabel", {
        number: i + 1,
      }),
      meals: basicMealTypes.map((type, index) => ({
        type,
        order: index + 1,
        foods: [],
        macrosTotal: {
          proteins: 0,
          carbs: 0,
          fats: 0,
          fibers: 0,
          calories: 0,
        },
      })),
    }));
  }, [optionsArray, basicMealTypes, t]);

  /**
   * Function to open the modals
   * @param {*} modal
   * @param {*} date
   */
  const handleOpenModal = (modal, date) => {
    submit(`/actions/refresh/?`, {
      method: "GET",
      body: { "modal[type]": modal, ...(date && { date }) },
    });
  };

  /**
   * Function to delete a custom meal
   * @param {*} e
   * @param {*} meal
   */
  const handleDeleteCustomMeal = (e, meal) => {
    e.preventDefault();
    e.stopPropagation();
    if (
      process.browser &&
      confirm(t("mealPlannerSimplifiedCalendar.deleteCustomMealConfirm"))
    ) {
      const { mealPlan: mealPlanId, athlete } = query || {};
      const body = {
        meal,
        bodyAction: "handle-custom-meal",
        handle: "delete",
        planMode: "simplified",
      };

      submit(
        `/actions/meal-planner/update-days-meal-plan?mealPlanId=${mealPlanId}&proxyAthlete=${athlete}`,
        {
          method: "POST",
          body,
        },
      );
    }
  };

  return (
    <div
      className={
        isFromNutritionPlan
          ? "main-meal-planner-calendar"
          : "mt-0! main-meal-planner-calendar"
      }
    >
      {mealTypes.map((mealType, mealIndex) => (
        <div key={`${mealType}-${mealIndex}`} className="meal-type-container">
          <MealPlannerMealTypeHeader
            mealType={mealType}
            basicMealTypes={basicMealTypes}
            viewMode={viewMode}
            onDeleteCustomMeal={handleDeleteCustomMeal}
          />
          <div className="meal-type-calendar">
            {displayOptions.map((option, optionIndex) => {
              // Find the meal of this type in the current option
              const specificMeal =
                option?.meals?.find((m) => m.type === mealType) || {};

              // Create a day-like object for MealPlannerCalendar
              const dayData = {
                date:
                  option.title ||
                  t("mealPlannerSimplifiedCalendar.optionLabel", {
                    number: optionIndex + 1,
                  }),
                hasData: true,
                dayType: tabState, // "training" or "rest"
                optionId: option.optionId, // Stable ID of the option
              };

              return (
                <MealPlannerCalendar
                  key={`${option.optionId || option._id || optionIndex}-${mealType}`}
                  query={query}
                  meal={specificMeal}
                  day={dayData}
                  week={tabState === "training" ? 1 : 2}
                  viewMode={viewMode}
                  basicMealsInformation={basicMealsInformation}
                  planMode="simplified"
                />
              );
            })}
          </div>
        </div>
      ))}
      {!viewMode && (
        <div
          className="add-custom-meal simplified"
          style={{
            width:
              numberOfOptions > 0
                ? `calc(${numberOfOptions} * 16.875rem + ${
                    numberOfOptions - 1
                  } * 0.5rem)`
                : "16.875rem",
          }}
        >
          <button
            onClick={() => {
              handleOpenModal("custom-meal");
            }}
          >
            <SvgIcon src="/img/new-icons/add-square-outline.svg" />
            <span>{t("mealPlannerSimplifiedCalendar.customMeal")}</span>
          </button>
        </div>
      )}
    </div>
  );
};

MealPlannerSimplifiedCalendar.propTypes = {
  mealPlan: PropTypes.object.isRequired,
  tabState: PropTypes.string.isRequired,
  viewMode: PropTypes.bool,
  isFromNutritionPlan: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  query: PropTypes.object,
  basicMealsInformation: PropTypes.array,
};
